import React from 'react';
import { 
  makeStyles,
  shorthands,
  Text,
  Badge,
} from '@fluentui/react-components';
import { MessageBar, MessageBarBody } from './ScrollableMessageBar';
import {
  Shield20Regular,
  ErrorCircle20Regular,
  Warning20Regular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
  },
  permissionMessage: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    ...shorthands.padding('12px', '16px'),
    backgroundColor: '#fffbeb',
    ...shorthands.border('1px', 'solid', '#fed7aa'),
    ...shorthands.borderRadius('6px'),
  },
  errorMessage: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    ...shorthands.padding('12px', '16px'),
    backgroundColor: '#fef2f2',
    ...shorthands.border('1px', 'solid', '#fecaca'),
    ...shorthands.borderRadius('6px'),
  },
  inlineBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    ...shorthands.gap('4px'),
  },
});

interface PermissionErrorHandlerProps {
  error: any;
  fallback?: React.ReactNode;
  variant?: 'message' | 'badge' | 'inline';
  showDetails?: boolean;
}

const checkPermissionError = (error: any) => {
  return error?.status === 403 || 
    error?.isPermissionError ||
    error?.message?.includes('PERMISSION_DENIED') ||
    error?.message?.includes('403');
}

const checkAuthError = (error: any) => {
  return error?.status === 401 ||
    error?.message?.includes('AUTHENTICATION_FAILED') ||
    error?.message?.includes('401');
}

export const PermissionErrorHandler: React.FC<PermissionErrorHandlerProps> = ({
  error,
  fallback,
  variant = 'message',
  showDetails = false,
}) => {
  const styles = useStyles();

  // Check if this is a permission error
  const isPermissionError = checkPermissionError(error);

  // Check for other specific error types
  const isAuthError = checkAuthError(error);

  if (!error) {
    return fallback ? <>{fallback}</> : null;
  }

  const getMessage = () => {
    if (isPermissionError) {
      return error?.userMessage || 
        'You do not have permission to access this resource. Contact your administrator if you need access.';
    }
    
    if (isAuthError) {
      return 'Authentication required. Please log in to continue.';
    }
    
    return error?.message || 'An error occurred while loading this content.';
  };

  const getIcon = () => {
    if (isPermissionError) {
      return <Shield20Regular data-testid="permission-error-icon-shield" />;
    }
    
    if (isAuthError) {
      return <Warning20Regular data-testid="permission-error-icon-warning" />;
    }
    
    return <ErrorCircle20Regular data-testid="permission-error-icon-error" />;
  };

  const getTitle = () => {
    if (isPermissionError) {
      return 'Admin Access Required';
    }
    
    if (isAuthError) {
      return 'Authentication Required';
    }
    
    return 'Error';
  };

  // Render based on variant
  switch (variant) {
    case 'badge':
      return (
        <Badge 
          appearance="filled" 
          className={styles.inlineBadge}
          data-testid="permission-error-badge"
          style={{
            backgroundColor: isPermissionError ? '#fffbeb' : '#fef2f2',
            color: isPermissionError ? '#d97706' : '#dc2626',
            border: `1px solid ${isPermissionError ? '#fed7aa' : '#fecaca'}`,
          }}
        >
          {getIcon()}
          <Text data-testid="permission-error-badge-title">{getTitle()}</Text>
        </Badge>
      );

    case 'inline':
      return (
        <Text style={{ 
          color: isPermissionError ? '#d97706' : '#dc2626',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }} data-testid="permission-error-inline">
          {getIcon()}
          <span data-testid="permission-error-inline-title">{getTitle()}</span>
        </Text>
      );

    case 'message':
    default:
      return (
        <div className={styles.container} data-testid="permission-error-message">
          <MessageBar 
            intent={isPermissionError ? "warning" : "error"}
            data-testid="permission-error-messagebar"
          >
            <MessageBarBody data-testid="permission-error-messagebar-body">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} data-testid="permission-error-message-layout">
                {getIcon()}
                <div data-testid="permission-error-message-content">
                  <Text style={{ fontWeight: '600' }} data-testid="permission-error-message-title">{getTitle()}</Text>
                  <div style={{ marginTop: '4px' }} data-testid="permission-error-message-body">
                    <Text style={{ fontSize: '14px' }} data-testid="permission-error-message-text">
                      {getMessage()}
                    </Text>
                  </div>
                  {showDetails && error?.message && (
                    <div style={{ marginTop: '8px' }} data-testid="permission-error-technical-details">
                      <Text style={{ fontSize: '12px', color: '#6b7280', fontFamily: 'monospace' }} data-testid="permission-error-technical-details-text">
                        Technical details: {error.message}
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            </MessageBarBody>
          </MessageBar>
        </div>
      );
  }
};

/**
 * Higher-order component that wraps components with permission error handling
 */
interface WithPermissionErrorHandlerProps {
  error: any;
  loading?: boolean;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

export const WithPermissionErrorHandler: React.FC<WithPermissionErrorHandlerProps> = ({
  error,
  loading = false,
  fallback,
  children,
}) => {
  if (loading) {
    return <>{children}</>;
  }

  if (error) {
    return <PermissionErrorHandler error={error} fallback={fallback} />;
  }

  return <>{children}</>;
};

/**
 * Hook for handling API errors with graceful permission error handling
 */
export const usePermissionErrorHandler = () => {
  const handleError = (error: any) => {
    console.error('API Error:', error);
    
    // Log permission errors differently
    if (error?.status === 403 || error?.isPermissionError) {
      console.warn('Permission denied - user lacks required permissions');
    }
    
    return error;
  };

  const isPermissionError = (error: any) => {
    return checkPermissionError(error);
  };

  const getErrorMessage = (error: any) => {
    if (isPermissionError(error)) {
      return error?.userMessage || 'Admin access required';
    }
    
    return error?.message || 'An error occurred';
  };

  return {
    handleError,
    isPermissionError,
    getErrorMessage,
  };
};

export default PermissionErrorHandler;